"use client"

import type React from "react"

import { useState, useEffect } from "react"
import { useRouter } from "next/navigation"
import { Navbar } from "@/components/navbar"
import { Footer } from "@/components/footer"
import type { OrderItem } from "@/lib/types"
import { apiStore } from "@/lib/api-store"
import { useCart } from "@/lib/cart-context"
import { formatNumber, formatPrice } from "@/lib/utils"
import { toast } from "sonner"
import { Trash2, Home } from "lucide-react"
import { GeolocationPicker } from "@/components/geolocation-picker"

export default function OrderPage() {
  const { cart, clearCart } = useCart()
  const [cartItems, setCartItems] = useState<OrderItem[]>([])
  const [formData, setFormData] = useState({
    customerName: "",
    customerAddress: "",
    customerPhone: "",
    mapsLocation: "",
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [orderConfirmed, setOrderConfirmed] = useState(false)
  const [orderId, setOrderId] = useState("")
  const [selectedLat, setSelectedLat] = useState<number | null>(null)
  const [selectedLng, setSelectedLng] = useState<number | null>(null)
  const router = useRouter()

  useEffect(() => {
    // Charger depuis sessionStorage (pour la compatibilité) ou depuis le contexte cart
    const sessionCart = JSON.parse(sessionStorage.getItem("cart") || "[]")

    if (sessionCart.length > 0) {
      setCartItems(sessionCart)
    } else if (cart.length > 0) {
      // Convertir le panier du contexte en OrderItem
      const orderItems: OrderItem[] = cart.map(item => ({
        productId: item.product._id || "",
        productName: item.product.name,
        quantity: item.quantity,
        price: item.price,
        originalPrice: item.originalPrice,
        promotionId: item.promotionId
      }))
      setCartItems(orderItems)
      // Sauvegarder dans sessionStorage pour la persistance
      sessionStorage.setItem("cart", JSON.stringify(orderItems))
    }

    // Charger les données du formulaire depuis localStorage
    const savedFormData = JSON.parse(localStorage.getItem("formData") || "{}")
    if (savedFormData) {
      setFormData((prev) => ({ ...prev, ...savedFormData }))
    }
  }, [cart])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    localStorage.setItem("formData", JSON.stringify({ ...formData, [name]: value }))
  }

  const handleLocationChange = (url: string, lat?: number, lng?: number) => {
    setFormData((prev) => ({ ...prev, mapsLocation: url }))
    if (lat !== undefined && lng !== undefined) {
      setSelectedLat(lat)
      setSelectedLng(lng)
    }
  }

  const removeItem = (productId: string) => {
    const updated = cartItems.filter((item) => item.productId !== productId)
    setCartItems(updated)
    sessionStorage.setItem("cart", JSON.stringify(updated))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.customerName || !formData.customerAddress || !formData.customerPhone) {
      toast.error("Veuillez remplir tous les champs requis")
      return
    }

    if (cartItems.length === 0) {
      toast.error("Votre panier est vide")
      return
    }

    setIsSubmitting(true)
    try {
      // Add order via API
      const total = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0)
      const order = await apiStore.addOrder({
        customerName: formData.customerName,
        customerAddress: formData.customerAddress,
        customerPhone: formData.customerPhone,
        mapsLocation: formData.mapsLocation,
        items: cartItems,
        total,
      })

      setOrderConfirmed(true)
      setOrderId(order._id || order.id || '')
      toast.success("Commande enregistrée avec succès!")

      // Vider le panier du localStorage (contexte cart)
      clearCart()

      // Vider le sessionStorage
      sessionStorage.removeItem("cart")
      setCartItems([])
    } catch (error: any) {
      toast.error(error.message || "Erreur lors de l'enregistrement de la commande")
    } finally {
      setIsSubmitting(false)
    }
  }

  const total = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0)
  const totalWithoutPromo = cartItems.reduce((sum, item) => sum + (item.originalPrice || item.price) * item.quantity, 0)
  const totalSavings = totalWithoutPromo - total
  const hasPromotions = cartItems.some(item => item.promotionId)

  if (orderConfirmed) {
    return (
      <main className="min-h-screen bg-white dark:bg-dark-background">
        <Navbar />

        <div className="container py-20">
          <div className="max-w-md mx-auto text-center">
            <div className="bg-success/20 text-success p-8 rounded-lg mb-6">
              <div className="text-6xl mb-4">✓</div>
              <h1 className="text-2xl font-bold text-foreground dark:text-dark-foreground mb-2">Commande Confirmée!</h1>
              <p className="text-foreground-secondary dark:text-dark-foreground-secondary">
                Numéro de commande: <span className="font-mono font-bold">{orderId}</span>
              </p>
            </div>

            <p className="text-foreground dark:text-dark-foreground mb-8">
              Merci pour votre achat! Nous traiterons votre commande rapidement.
            </p>

            <button
              onClick={() => router.push("/boutique")}
              className="w-full bg-primary text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-dark transition flex items-center justify-center gap-2"
            >
              <Home size={20} /> Voir d'autres produits
            </button>
          </div>
        </div>

        <Footer />
      </main>
    )
  }

  return (
    <main className="min-h-screen bg-white dark:bg-dark-background">
      <Navbar />

      <div className="container py-12">
        <h1 className="text-3xl font-bold mb-8 text-foreground dark:text-dark-foreground">Formulaire de Commande</h1>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Form */}
          <div className="md:col-span-2">
            <form
              onSubmit={handleSubmit}
              className="space-y-6 bg-background-secondary dark:bg-dark-background-secondary p-6 rounded-lg border border-border dark:border-dark-border"
            >
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground dark:text-dark-foreground mb-2">
                    Nom
                  </label>
                  <input
                    type="text"
                    name="customerName"
                    value={formData.customerName}
                    onChange={handleInputChange}
                    className="input-field"
                    placeholder="KABORE Jean"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground dark:text-dark-foreground mb-2">
                    Téléphone (WhatsApp | Appel) *
                  </label>
                  <input
                    type="tel"
                    name="customerPhone"
                    value={formData.customerPhone}
                    onChange={handleInputChange}
                    className="input-field"
                    placeholder="+226 76 XX XX XX"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground dark:text-dark-foreground mb-2">
                  Lieu de Livraison *
                </label>
                <input
                  type="text"
                  name="customerAddress"
                  value={formData.customerAddress}
                  onChange={handleInputChange}
                  className="input-field"
                  placeholder="Ex : Ouagadougou, Somgandé, vers le marché"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground dark:text-dark-foreground mb-2">
                  Position de Livraison
                </label>
                <GeolocationPicker value={formData.mapsLocation} onChange={handleLocationChange} />
              </div>

              <button
                type="submit"
                disabled={isSubmitting || cartItems.length === 0}
                className="w-full bg-primary text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Traitement..." : "Valider la Commande"}
              </button>
            </form>
          </div>

          {/* Cart Summary */}
          <div>
            <div className="bg-background-secondary dark:bg-dark-background-secondary p-6 rounded-lg sticky top-20 border border-border dark:border-dark-border">
              <h2 className="text-xl font-bold mb-4 text-foreground dark:text-dark-foreground">Résumé du Panier</h2>

              {cartItems.length === 0 ? (
                <p className="text-foreground-secondary dark:text-dark-foreground-secondary">Votre panier est vide</p>
              ) : (
                <>
                  <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
                    {cartItems.map((item) => (
                      <div
                        key={item.productId}
                        className="flex justify-between items-start pb-3 border-b border-border dark:border-dark-border"
                      >
                        <div className="flex-1">
                          <p className="font-medium text-foreground dark:text-dark-foreground text-sm">
                            {item.productName}
                          </p>
                          <div className="flex items-center gap-2">
                            <p className="text-xs text-foreground-secondary dark:text-dark-foreground-secondary">
                              {formatNumber(item.quantity)}x {formatPrice(item.price)}
                            </p>
                            {item.promotionId && item.originalPrice && item.originalPrice > item.price && (
                              <span className="text-xs text-red-600 dark:text-red-400 font-semibold">
                                🏷️ Promo!
                              </span>
                            )}
                          </div>
                          {item.promotionId && item.originalPrice && item.originalPrice > item.price && (
                            <p className="text-xs text-gray-500 line-through">
                              {formatPrice(item.originalPrice)}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => removeItem(item.productId)}
                          className="text-error hover:bg-error/20 p-1 rounded transition"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-border dark:border-dark-border pt-4">
                    {hasPromotions && totalSavings > 0 && (
                      <>
                        <div className="flex justify-between mb-2 text-sm">
                          <span className="text-foreground-secondary dark:text-dark-foreground-secondary">Sous-total:</span>
                          <span className="line-through text-gray-500">{formatPrice(totalWithoutPromo)}</span>
                        </div>
                        <div className="flex justify-between mb-2 text-sm">
                          <span className="text-green-600 dark:text-green-400 font-medium">Économies (Promos):</span>
                          <span className="text-green-600 dark:text-green-400 font-bold">-{formatPrice(totalSavings)}</span>
                        </div>
                      </>
                    )}
                    <div className="flex justify-between mb-2">
                      <span className="text-foreground-secondary dark:text-dark-foreground-secondary font-semibold">Total:</span>
                      <span className="font-bold text-primary text-lg">{formatPrice(total)}</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </main>
  )
}
